/**
 * DeviceCommands — Etapa 4.1 (Limpeza NOC).
 *
 * Grade de comandos/ações do device, reaproveitada pelo painel direito
 * (desktop) e pelo bottom-sheet (mobile). NÃO contém lógica de escrita
 * própria: cada botão chama `onCommand(action, deviceId)`, que no MapView
 * é o MESMO `handlePopupAction` que o popup usava (confirm + enqueue já
 * existentes). Nenhuma chamada nova é criada aqui.
 */
import { t } from "../../i18n/index.js";

// MAP-MANAGE-1C — ações que o backend bloqueia em device protegido
// (PROTECTED_COMMAND_TYPES + OTA). 'move'/'open'/'select'/'history' seguem.
const PROTECTED_ACTIONS = new Set([
  "relay_on", "relay_off", "relay_reboot", "reboot", "ota", "config",
]);

export default function DeviceCommands({ device, onCommand }) {
  if (!device || !onCommand) return null;
  const id = device.device_id;
  const relay = device.relay;
  const showRelayOn = relay !== "on";
  const showRelayOff = relay !== "off";
  const isProtected = !!device.ota_protected;

  const Btn = ({ action, label, variant }) => {
    const blocked = isProtected && PROTECTED_ACTIONS.has(action);
    return (
      <button
        type="button"
        className={`ipwd-cmd ${variant || ""}`}
        onClick={() => onCommand(action, id)}
        disabled={blocked}
        title={blocked
          ? "Device protegido (produção) — ação crítica bloqueada pelo backend"
          : undefined}
        style={blocked ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="ipwd-rp-section">
      <div className="ipwd-rp-section-title">Comandos</div>

      {/* MAP-MANAGE-1C — aviso de proteção (backend é a fonte da verdade) */}
      {isProtected && (
        <div
          className="ipwd-rp-hint"
          style={{
            color: "var(--noc-warn, #FFB224)",
            border: "1px solid rgba(255,178,36,.3)",
            borderRadius: 6, padding: "6px 8px", marginBottom: 8,
            fontSize: 11,
          }}
        >
          🔒 Protegido contra ações críticas — relay/reboot/config/OTA
          bloqueados pelo backend (denylist de produção).
        </div>
      )}

      {/* Relé — só mostra o estado oposto ao atual */}
      <div className="ipwd-cmd-row">
        {showRelayOn && (
          <Btn action="relay_on" variant="is-on" label={`▶ ${t("device.relayOn")}`} />
        )}
        {showRelayOff && (
          <Btn action="relay_off" variant="is-off" label={`■ ${t("device.relayOff")}`} />
        )}
        {!showRelayOn && !showRelayOff && (
          <span className="ipwd-rp-na">{t("device.relayNoState")}</span>
        )}
      </div>

      <div className="ipwd-cmd-row">
        <Btn action="relay_reboot" variant="is-relay-reboot" label={`⟲ ${t("device.relayReboot")}`} />
        <Btn action="reboot" variant="is-reboot" label={`⟳ ${t("device.espReboot")}`} />
      </div>

      <div className="ipwd-cmd-row is-3">
        <Btn action="ota" variant="is-ota" label="⬇ OTA" />
        <Btn action="move" variant="is-move" label={`✥ ${t("device.move")}`} />
        <Btn action="open" variant="is-open" label={`↗ ${t("device.open")}`} />
      </div>

      <div className="ipwd-cmd-row is-3">
        <Btn action="select" variant="is-select" label={`✓ ${t("device.addToBatch")}`} />
        <Btn action="history" variant="is-history" label={`📋 ${t("device.history")}`} />
        <Btn action="config" variant="is-config" label={`⚙ ${t("device.configure")}`} />
      </div>
    </div>
  );
}
